import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import LogoutIcon from "@mui/icons-material/Logout";

const SessionContext = createContext();

export const checkSessionStatus = async () => {
  const endpoint = "/session/status.json";

  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "GET",
      credentials: "include",
    });

    const data = await response.json();
    if (response.ok && data.success) {
      const authenticated = data.data?.authenticated || false;
      let timeRemaining = 0;
      if (authenticated && data.data?.expiresAt) {
        const expiresAt = new Date(data.data.expiresAt);
        timeRemaining = Math.max(
          0,
          Math.floor((expiresAt - new Date()) / 1000),
        );
      }
      return {
        active: authenticated,
        timeRemaining,
        success: true,
      };
    } else {
      return {
        active: false,
        timeRemaining: 0,
        success: false,
        httpStatus: response.status,
      };
    }
  } catch (error) {
    return {
      active: false,
      timeRemaining: 0,
      success: false,
      error: error.message,
      isNetworkError: true,
    };
  }
};

export const renewSession = async () => {
  const endpoint = "/session/auth/renew.json";

  try {
    const response = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
      method: "POST",
      credentials: "include",
    });

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        timeRemaining: data.timeRemaining || 1800,
        data: data,
      };
    } else {
      return {
        success: false,
        error: data.message || "Failed to renew session",
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

const SessionExpiryDialog = ({ open, timeRemaining, onRenew, onLogout }) => {
  const [countdown, setCountdown] = useState(timeRemaining);

  useEffect(() => {
    if (open && timeRemaining > 0) {
      setCountdown(timeRemaining);
      const timer = setInterval(() => {
        setCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, timeRemaining]);

  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "warning.main",
        }}
      >
        <WarningIcon />
        Session Expiring Soon
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your session is about to expire in approximately{" "}
          <strong>
            {Math.floor(countdown / 60)} minute
            {Math.floor(countdown / 60) !== 1 ? "s" : ""} and {countdown % 60}{" "}
            second{countdown % 60 !== 1 ? "s" : ""}
          </strong>{" "}
          .
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Would you like to extend your session and stay logged in?
        </Typography>
        <Alert sx={{ fontSize: "0.875rem" }}>
          Click "Stay Logged In" to extend your session, or "Logout Now" to end
          your session.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onLogout}
          variant="contained"
          startIcon={<LogoutIcon />}
        >
          Logout Now
        </Button>
        <Button onClick={onRenew} color="primary" variant="contained" autoFocus>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SessionExpiredPage = ({ onLoginClick }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        textAlign: "center",
        p: 3,
      }}
    >
      <Alert severity="warning" sx={{ mb: 3, maxWidth: 500 }}>
        {/* <Typography variant="h6" sx={{ mb: 1 }}>
                    Session Expired
                </Typography> */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Your session has expired due to inactivity.
        </Typography>
      </Alert>

      <Button
        variant="contained"
        size="large"
        onClick={onLoginClick}
        sx={{ mt: 2 }}
      >
        Click Here to Log Back In
      </Button>
    </Box>
  );
};

export const useSessionManager = () => {
  const [sessionActive, setSessionActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    sessionStorage.getItem("isAdmin") === "true",
  );
  const [isUser, setIsUser] = useState(
    sessionStorage.getItem("isUser") === "true",
  );

  useEffect(() => {
    const checkAuthStatus = () => {
      setIsAdmin(sessionStorage.getItem("isAdmin") === "true");
      setIsUser(sessionStorage.getItem("isUser") === "true");
    };

    checkAuthStatus();

    window.addEventListener("storage", checkAuthStatus);
    window.addEventListener("adminLoginChange", checkAuthStatus);
    window.addEventListener("userLoginChange", checkAuthStatus);

    return () => {
      window.removeEventListener("storage", checkAuthStatus);
      window.removeEventListener("adminLoginChange", checkAuthStatus);
      window.removeEventListener("userLoginChange", checkAuthStatus);
    };
  }, []);

  const handleSessionExpired = useCallback(() => {
    sessionStorage.removeItem("isAdmin");
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("isUser");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("authToken");

    setSessionActive(false);
    setShowExpiryDialog(false);
    setSessionExpired(true);

    window.dispatchEvent(new Event("adminLoginChange"));
    window.dispatchEvent(new Event("userLoginChange"));
    window.dispatchEvent(new Event("sessionExpired"));

    const redirectPath = isAdmin ? "/About/AdminLogin" : "/About/UserLogin";
    setTimeout(() => {
      window.location.href = redirectPath;
    }, 100);
  }, [isAdmin]);

  const checkSession = useCallback(async () => {
    if (!isAdmin && !isUser) return;

    try {
      const status = await checkSessionStatus();

      if (status.success && status.active) {
        setSessionActive(true);
        setTimeRemaining(status.timeRemaining);
        setSessionExpired(false);

        if (status.timeRemaining <= 600 && status.timeRemaining > 0) {
          setShowExpiryDialog(true);
        } else if (status.timeRemaining > 600) {
          setShowExpiryDialog(false);
        } else if (status.timeRemaining <= 0) {
          setShowExpiryDialog(false);
          handleSessionExpired();
        }
      } else if (
        status.success === false &&
        status.active === false &&
        !status.isNetworkError
      ) {
        setSessionActive(false);
        setTimeRemaining(0);
        setShowExpiryDialog(false);

        if (isAdmin || isUser) {
          handleSessionExpired();
        }
      } else if (status.httpStatus === 401 || status.httpStatus === 403) {
        setSessionActive(false);
        setTimeRemaining(0);
        setShowExpiryDialog(false);
        handleSessionExpired();
      } else {
        setSessionActive(false);
        setTimeRemaining(0);
        setShowExpiryDialog(false);
      }
    } catch (error) {
      console.error("Error checking session status:", error);
      setSessionActive(false);
      setTimeRemaining(0);
      setShowExpiryDialog(false);
    }
  }, [isAdmin, isUser, handleSessionExpired]);

  const handleRenewSession = async () => {
    const result = await renewSession();

    if (result.success) {
      setTimeRemaining(result.timeRemaining);
      setShowExpiryDialog(false);
      setSessionActive(true);
    } else {
      handleLogout();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("isAdmin");
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("isUser");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("authToken");

    setSessionActive(false);
    setShowExpiryDialog(false);
    setSessionExpired(false);
    setIsAdmin(false);
    setIsUser(false);

    window.dispatchEvent(new Event("adminLoginChange"));
    window.dispatchEvent(new Event("userLoginChange"));
  };

  useEffect(() => {
    if (isAdmin || isUser) {
      checkSession();

      const interval = setInterval(checkSession, 10000);

      return () => {
        clearInterval(interval);
      };
    } else {
      setSessionActive(false);
      setTimeRemaining(0);
      setShowExpiryDialog(false);
    }
  }, [isAdmin, isUser, checkSession]);

  return {
    sessionActive,
    timeRemaining,
    showExpiryDialog,
    sessionExpired,
    handleRenewSession,
    handleLogout,
    SessionExpiryDialogComponent: SessionExpiryDialog,
    SessionExpiredPageComponent: SessionExpiredPage,
  };
};

export const SessionProvider = ({ children }) => {
  const sessionManager = useSessionManager();

  return (
    <SessionContext.Provider value={sessionManager}>
      {children}
      <sessionManager.SessionExpiryDialogComponent
        open={sessionManager.showExpiryDialog}
        timeRemaining={sessionManager.timeRemaining}
        onRenew={sessionManager.handleRenewSession}
        onLogout={() => {
          sessionManager.handleLogout();
          setTimeout(() => {
            window.location.href = "/";
          }, 100);
        }}
      />
      {sessionManager.sessionExpired && (
        <sessionManager.SessionExpiredPageComponent
          onLoginClick={() => {
            window.location.href = "/About/AdminLogin";
          }}
        />
      )}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

export const SessionStatusChecker = () => {
  React.useEffect(() => {
    checkSessionStatus();
  }, []);

  return null;
};

export const SessionRenewal = renewSession;
export const WithSessionManagement = ({ children }) => {
  const session = useSessionManager();

  if (session.sessionExpired) {
    return (
      <session.SessionExpiredPageComponent
        onLoginClick={() => {
          window.location.href = "/About/AdminLogin";
        }}
      />
    );
  }

  return (
    <>
      {children}
      <session.SessionExpiryDialogComponent
        open={session.showExpiryDialog}
        timeRemaining={session.timeRemaining}
        onRenew={session.handleRenewSession}
        onLogout={() => {
          session.handleLogout();
          setTimeout(() => {
            window.location.href = "/";
          }, 100);
        }}
      />
    </>
  );
};

export const useSessionStatus = () => {
  const session = useSessionManager();
  return {
    isSessionActive: session.sessionActive,
    timeRemaining: session.timeRemaining,
    isExpiringSoon: session.showExpiryDialog,
    isExpired: session.sessionExpired,
    renewSession: session.handleRenewSession,
    logout: session.handleLogout,
  };
};
